import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { XIcon, PencilIcon, ChevronLeftIcon, ChevronRightIcon, LayoutGridIcon, StarIcon, SparklesIcon } from 'lucide-react';
import { genreColors, formatIcons } from '../author-bookshelf/constants';
import { categoryLabels, stockColors, stockLabels } from './data';
import { useRoleTheme } from '../../../hooks/useRoleTheme';
interface ItemDisplayProps {
  item: any;
  darkMode: boolean;
  textPrimary: string;
  textSecondary: string;
  onClose: () => void;
  onEdit: (id: string) => void;
}
export const ItemDisplay: React.FC<ItemDisplayProps> = ({
  item,
  darkMode,
  textPrimary,
  textSecondary,
  onClose,
  onEdit
}) => {
  const rt = useRoleTheme();
  return <div className="h-full flex flex-col relative" data-id="element-20133" data-mp="1oi9wmt4kvv41">
      <div className="flex-1 overflow-y-auto" data-id="element-20134" data-mp="2m0s3xysenbju">
        <div className="flex justify-center pt-3 pb-1 md:hidden" data-id="element-20135" data-mp="2m1l2ich7muqw">
          <div className={`w-10 h-1 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} data-id="element-20136" data-mp="2h88hsdfnj83f" />
        </div>
        <button onClick={onClose} className={`absolute top-3 right-3 p-2 rounded-full ${darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'} transition-colors`} data-id="element-20137" data-mp="2y0jd38qb2jj4">
          <XIcon className="h-4 w-4" data-id="element-20138" data-mp="2t6owie5zyko9" />
        </button>
        <div className="px-5 pb-6 pt-3 md:pt-5" data-id="element-20139" data-mp="2t541knfrszpv">
          <div className="flex gap-4 items-start" data-id="element-20140" data-mp="2f6mgyyl5w3c6">
            <div className={`flex-shrink-0 w-24 overflow-hidden rounded-xl shadow-lg ${isBook ? 'h-36' : 'h-24'}`} data-id="element-20141" data-mp="2fa1ys967zqha">
              <img src={item.coverImage} alt={item.title || item.name} className="w-full h-full object-cover" data-id="element-20142" data-mp="25ajiq3tfkoui" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-20143" data-mp="3hvcrmpejnb1">
              <h3 className={`font-bold text-base leading-tight line-clamp-2 mb-1 ${textPrimary}`} data-id="element-20144" data-mp="3g22zyjhsmyt">
                {item.title || item.name}
              </h3>
              {isBook ? <>
                  {item.series && <p className="text-xs text-purple-500 mb-1 font-medium" data-id="element-20145" data-mp="1zei9amuaulf">
                      {item.series} #{item.seriesNumber}
                    </p>}
                  <div className="flex flex-wrap gap-1 mb-1.5" data-id="element-20146" data-mp="3o8z6csbcdfvk">
                    {item.genre && <span className={`text-xs px-2 py-0.5 ${rt.badgeRadius} ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'}`} data-id="element-20147" data-mp="ppimnk7uqydh">
                        {item.genre}
                      </span>}
                  </div>
                  <div className="flex items-center gap-1" data-id="element-20148" data-mp="b0w8hltyocnu">
                    <StarIcon className="h-3.5 w-3.5 text-amber-500 fill-current" data-id="element-20149" data-mp="18gry3z16nx7p" />
                    <span className={`text-sm font-semibold ${textPrimary}`} data-id="element-20150" data-mp="13qbhk622fvh4">
                      {item.rating}
                    </span>
                  </div>
                </> : <>
                  <span className={`text-xs px-2 py-0.5 ${rt.badgeRadius} font-medium ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'}`} data-id="element-20151" data-mp="1g015xnu1jnun">
                    {categoryLabels[item.category] || item.category}
                  </span>
                  <div className="mt-1.5 flex items-center gap-2" data-id="element-20152" data-mp="1uyd2luheywlt">
                    <span className={`text-lg font-bold ${textPrimary}`} data-id="element-20153" data-mp="1uw0mznz4oybx">
                      ${item.price?.toFixed(2)}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 ${rt.badgeRadius} text-white ${stockColors[item.stockStatus] || 'bg-gray-500'}`} data-id="element-20154" data-mp="23r6k7ykc282u">
                      {stockLabels[item.stockStatus] || item.stockStatus}
                    </span>
                  </div>
                </>}
            </div>
          </div>
          <div className="mt-4 flex gap-2" data-id="element-20155" data-mp="3fejbbedsj97x">
            <button onClick={() => {
            onEdit(item.id);
            onClose();
          }} className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-id="element-20156" data-mp="3fd85l90sbre6">
              <PencilIcon className="h-4 w-4" data-id="element-20157" data-mp="3k6az5h5kb0ud" />
              {isBook ? 'Edit Book' : 'Edit Product'}
            </button>
            <button onClick={onClose} className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${darkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-20158" data-mp="2rgu1a3z6arqr">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>;
};
export const CoverCarousel: React.FC<{
  items: any[];
  darkMode: boolean;
  onItemClick: (item: any) => void;
  onHoverEnter: (id: string) => void;
  onHoverLeave: () => void;
}> = ({
  items,
  darkMode,
  onItemClick,
  onHoverEnter,
  onHoverLeave
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const checkScroll = () => {
    if (scrollRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      if (el) el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [items]);
  const scroll = (dir: 'left' | 'right') => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: dir === 'left' ? -scrollRef.current.clientWidth * 0.75 : scrollRef.current.clientWidth * 0.75,
        behavior: 'smooth'
      });
      setTimeout(checkScroll, 350);
    }
  };
  return <div className="relative group" data-id="element-20159" data-mp="1vjfxstue9swe">
      <div ref={scrollRef} className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} onScroll={checkScroll} data-id="element-20160" data-mp="1vid30atyb6b7">
        {items.map((item: any) => {
        const isBook = item.itemType === 'book';
        return <div key={item.id} className={`flex-shrink-0 cursor-pointer group/item ${isBook ? 'w-28 sm:w-32' : 'w-32 sm:w-36'}`} onClick={() => onItemClick(item)} onMouseEnter={() => onHoverEnter(item.id)} onMouseLeave={onHoverLeave} data-id="element-20161" data-mp="1bvoyr7k7vl0l">
              <div className={`w-full ${isBook ? 'aspect-[2/3]' : 'aspect-square'} bg-center bg-cover rounded-lg shadow-md transition-transform duration-200 ease-in-out group-hover/item:scale-[1.04] group-hover/item:shadow-xl`} style={{
            backgroundImage: `url('${item.coverImage}')`
          }} title={item.title || item.name} data-id="element-20162" data-mp="xve6q17680du" />
            </div>;
      })}
      </div>
      {canScrollLeft && <button onClick={() => scroll('left')} className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 p-2 rounded-full shadow-md ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'}`} data-id="element-20163" data-mp="2qt1hu5pj62fq">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-20164" data-mp="34kpxuk2whb1z" />
        </button>}
      {canScrollRight && <button onClick={() => scroll('right')} className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 p-2 rounded-full shadow-md ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'}`} data-id="element-20165" data-mp="282tnod3t6mbo">
          <ChevronRightIcon className="h-4 w-4" data-id="element-20166" data-mp="10i9o4h7lsx1t" />
        </button>}
    </div>;
};
export const MixedGrid: React.FC<{
  items: any[];
  darkMode: boolean;
  onItemClick: (item: any) => void;
  onHoverEnter: (id: string) => void;
  onHoverLeave: () => void;
}> = ({
  items,
  darkMode,
  onItemClick,
  onHoverEnter,
  onHoverLeave
}) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  return <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-id="element-20167" data-mp="1jfkltxdl7if5">
      {items.map((item: any) => {
      const isBook = item.itemType === 'book';
      return <div key={item.id} className="cursor-pointer group" onClick={() => onItemClick(item)} onMouseEnter={() => !isMobile && onHoverEnter(item.id)} onMouseLeave={onHoverLeave} data-id="element-20168" data-mp="1t76v3o6d77f9">
            <div className={`w-full ${isBook ? 'aspect-[2/3]' : 'aspect-square'} bg-center bg-cover rounded-lg shadow-md transition-transform duration-200 ease-in-out group-hover:scale-[1.03] group-hover:shadow-xl`} style={{
          backgroundImage: `url('${item.coverImage}')`
        }} title={item.title || item.name} data-id="element-20169" data-mp="232ic6d58wqq6" />
          </div>;
    })}
    </div>;
};
// Spine view — books only
export const MixedInfoModal = ItemDisplay;
export const SpineView: React.FC<{
  items: any[];
  darkMode: boolean;
  booksPerShelf: number;
  hoveredBookId: string | null;
  setHoveredBookId: (id: string | null) => void;
  mousePosition: {
    x: number;
    y: number;
  };
  onBookClick: (book: any) => void;
}> = ({
  items,
  darkMode,
  booksPerShelf,
  hoveredBookId,
  setHoveredBookId,
  mousePosition,
  onBookClick
}) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const shelves = chunkBooksIntoShelves(items, booksPerShelf);
  return <>
      {shelves.map((shelf, idx) => <div key={idx} className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 mb-8`} data-id="element-20170" data-mp="2dnfv9budzakk" data-mpi={idx}>
          <div className="relative" data-id="element-20171" data-mp="23nowgxysm9eo">
            <div className="flex items-end gap-2 overflow-x-auto pb-6 scrollbar-thin" style={{
          minHeight: 260
        }} data-id="element-20172" data-mp="23oqclguo1m39">
              {shelf.map((book: any) => <div key={book.id} onClick={() => onBookClick(book)} onMouseEnter={() => !isMobile && setHoveredBookId(book.id)} onMouseLeave={() => setHoveredBookId(null)} className="relative flex flex-col items-center justify-center text-white font-medium rounded transition-transform duration-200 ease-in-out cursor-pointer hover:scale-105 select-none flex-shrink-0" style={{
            width: book.edition === 'special' ? 68 : 56,
            height: book.edition === 'special' ? 250 : 220,
            fontSize: '0.85rem',
            backgroundColor: genreColors[!book.coverImage ? book.genre : ''] || genreColors.default,
            backgroundImage: book.coverImage ? `url(${book.coverImage})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            marginBottom: 4
          }} title={book.title} data-id="element-20173" data-mp="2we71i4j2ukuq">
                  {!book.coverImage && <div className="flex flex-col items-center px-1 w-full h-full justify-center" style={{
              writingMode: 'vertical-rl'
            }} data-id="element-20174" data-mp="tp5o5khdt4w0">
                      <div className="w-full truncate" data-id="element-20175" data-mp="253jagkwhlc9x">{book.title}</div>
                    </div>}
                  <div className="absolute bottom-1 left-1 inline-flex items-center justify-center p-1 rounded bg-black bg-opacity-70 text-white text-xs z-10" data-id="element-20176" data-mp="12qn29hlcqqgh">
                    {formatIcons[book.format]}
                  </div>
                </div>)}
              <div className={`absolute bottom-0 left-0 right-0 h-2 ${darkMode ? 'bg-purple-900' : 'bg-purple-600'} rounded-full`} data-id="element-20177" data-mp="1gpmpqlx4ozb2" />
            </div>
          </div>
          {!isMobile && hoveredBookId && (() => {
        const hovered = shelf.find((b: any) => b.id === hoveredBookId);
        return hovered ? <BookHoverCard book={hovered} darkMode={darkMode} mousePosition={mousePosition} data-id="element-20178" data-mp="xsd7h83jhrve" /> : null;
      })()}
        </div>)}
    </>;
};